import { Box } from "@chakra-ui/react";
import { call, colors, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import {
  MubinsoCheckItem,
  MubinsoCtaButton,
  MubinsoInfoBox,
  MubinsoNumberedSteps,
  MubinsoScreenLayout,
} from "../Mubinso/MubinsoUI";
import { CREMA_PHONE, CREMA_TERMINAL_DEATH, CREMA_TERMINAL_RELOC } from "./crematoriumConstants";

/**
 * 화장장 퍼널 종료 화면.
 * - death: 임종 상태 → 빠른 화장예약 안내 + 전화 CTA
 * - relocation: 개장이장 → 절차 안내 + 화장장 번호(있으면) 또는 안내 문구(없으면)
 */
export function CrematoriumTerminalScreen({
  type,
  crematoriumPhone,
}: {
  type: "death" | "relocation";
  /** 개장이장 화면 전용. 세션에 저장된 화장장 전화번호. 없으면 null. */
  crematoriumPhone?: string | null;
}) {
  if (type === "death") return <DeathScreen />;
  return <RelocationScreen crematoriumPhone={crematoriumPhone ?? null} />;
}

// ---------------------------------------------------------------------------
// 3-1: 임종 → 빠른 화장예약
// ---------------------------------------------------------------------------
function DeathScreen() {
  const c = CREMA_TERMINAL_DEATH;
  return (
    <MubinsoScreenLayout
      footer={
        <MubinsoCtaButton
          label={`${c.cta} ${CREMA_PHONE}`}
          onClick={() => {
            customEvent({
              newGtm: { path: "crema_funnel", trigger_and_target: "term_death-call" },
            });
            call(CREMA_PHONE);
          }}
        />
      }
    >
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {c.headline.split(c.highlightText).map((part, i, arr) => (
          <span key={i}>
            {part}
            {i < arr.length - 1 && (
              <span style={{ color: colors.orange650 }}>{c.highlightText}</span>
            )}
          </span>
        ))}
      </Box>

      <Spacing gap={20} />
      <MubinsoInfoBox variant="peach">
        <span dangerouslySetInnerHTML={{ __html: c.callout }} />
      </MubinsoInfoBox>

      <Spacing gap={20} />
      <BaseStack width="100%" spacing={12}>
        {c.checks.map((text) => (
          <MubinsoCheckItem key={text}>{text}</MubinsoCheckItem>
        ))}
      </BaseStack>
    </MubinsoScreenLayout>
  );
}

// ---------------------------------------------------------------------------
// 3-2: 개장이장 안내
// ---------------------------------------------------------------------------
function RelocationScreen({ crematoriumPhone }: { crematoriumPhone: string | null }) {
  const c = CREMA_TERMINAL_RELOC;
  const hasPhone = !!crematoriumPhone;

  return (
    <MubinsoScreenLayout
      footer={
        hasPhone ? (
          <MubinsoCtaButton
            label={`${c.cta} ${crematoriumPhone}`}
            onClick={() => {
              customEvent({
                newGtm: { path: "crema_funnel", trigger_and_target: "term_reloc-call" },
              });
              call(crematoriumPhone);
            }}
          />
        ) : undefined
      }
    >
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {c.headline}
      </Box>

      <Spacing gap={24} />
      <MubinsoNumberedSteps items={[...c.steps]} />

      {!hasPhone && (
        <>
          <Spacing gap={24} />
          <MubinsoInfoBox variant="gray">
            지금 안내드린 곳은 <b>고이 상조</b>입니다.
            <br />
            화장장을 직접 검색해주세요.
          </MubinsoInfoBox>
        </>
      )}
    </MubinsoScreenLayout>
  );
}
